import { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TableSortLabel,
  Checkbox,
  Paper,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { tokens } from '../../theme/tokens';
import SkeletonLoader from './SkeletonLoader';
import EmptyState from './EmptyState';
import ErrorMessage from './ErrorMessage';

/**
 * DataTable Component
 * 
 * A reusable, accessible data table with server-side pagination, sorting, and filtering.
 * Follows WCAG 2.1 AA guidelines with proper table semantics and keyboard navigation.
 * 
 * @component
 * @example
 * ```jsx
 * <DataTable
 *   columns={[
 *     { key: 'name', header: 'Name', sortable: true },
 *     { key: 'email', header: 'Email', width: 250 },
 *     { key: 'status', header: 'Status', render: (row) => <Chip label={row.status} /> }
 *   ]}
 *   rows={users}
 *   total={totalCount}
 *   page={page}
 *   pageSize={pageSize}
 *   onPageChange={handlePageChange}
 *   onPageSizeChange={handlePageSizeChange}
 *   onSortChange={handleSortChange}
 *   getRowId={(row) => row.id}
 * />
 * ```
 */
const DataTable = ({
  columns,
  rows,
  total,
  page,
  pageSize,
  sortBy,
  sortDir = 'asc',
  loading = false,
  error = null,
  onPageChange,
  onPageSizeChange,
  onSortChange,
  onSelectRows,
  getRowId,
  actionsSlot,
  emptyMessage = 'No data available',
  ariaLabel = 'data table',
  onRetry,
}) => {
  const theme = useTheme();
  const [selected, setSelected] = useState([]);

  // Handle select all
  const handleSelectAll = useCallback((event) => {
    if (event.target.checked) {
      const newSelected = rows.map((row) => getRowId(row));
      setSelected(newSelected);
      onSelectRows?.(newSelected);
    } else {
      setSelected([]);
      onSelectRows?.([]);
    }
  }, [rows, getRowId, onSelectRows]);

  // Handle single row selection
  const handleSelectRow = useCallback((id) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selected, id];
    } else {
      newSelected = selected.filter((item) => item !== id);
    }

    setSelected(newSelected);
    onSelectRows?.(newSelected);
  }, [selected, onSelectRows]);

  // Handle sort
  const handleSort = useCallback((columnKey) => {
    const isAsc = sortBy === columnKey && sortDir === 'asc';
    onSortChange?.(columnKey, isAsc ? 'desc' : 'asc');
  }, [sortBy, sortDir, onSortChange]);

  // Determine if all rows are selected
  const isAllSelected = useMemo(() => {
    return rows.length > 0 && selected.length === rows.length;
  }, [rows.length, selected.length]);

  const isIndeterminate = useMemo(() => {
    return selected.length > 0 && selected.length < rows.length;
  }, [selected.length, rows.length]);

  // Loading state
  if (loading) {
    return (
      <Box
        role="status"
        aria-live="polite"
        aria-label="Loading data"
      >
        <SkeletonLoader variant="table" count={5} />
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <ErrorMessage
        title="Unable to load data"
        message={error}
        actionLabel="Retry"
        onAction={onRetry}
        severity="error"
      />
    );
  }

  // Empty state
  if (!rows || rows.length === 0) {
    return (
      <EmptyState
        title="No data available"
        description={emptyMessage || "There's nothing to display here yet."}
        variant="illustrated"
      />
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        overflow: 'hidden',
        borderRadius: `${tokens.radius.lg}px`,
        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
      }}
    >
      <Box
        sx={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': {
            height: 8,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: alpha(theme.palette.primary.main, 0.05),
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: alpha(theme.palette.primary.main, 0.2),
            borderRadius: 4,
            '&:hover': {
              backgroundColor: alpha(theme.palette.primary.main, 0.3),
            },
          },
        }}
        role="region"
        aria-label={ariaLabel}
      >
        <Table
          sx={{
            minWidth: 650,
          }}
          aria-label={ariaLabel}
        >
          <TableHead>
            <TableRow
              sx={{
                backgroundColor: alpha(theme.palette.primary.main, 0.05),
              }}
            >
              {onSelectRows && (
                <TableCell
                  padding="checkbox"
                  sx={{ width: 48 }}
                >
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={handleSelectAll}
                    inputProps={{
                      'aria-label': 'Select all rows',
                    }}
                    sx={{
                      '&:focus-visible': {
                        outline: `2px solid ${theme.palette.primary.main}`,
                        outlineOffset: 2,
                      },
                    }}
                  />
                </TableCell>
              )}
              {columns.map((column) => (
                <TableCell
                  key={String(column.key)}
                  align={column.align || 'left'}
                  sx={{
                    width: column.width,
                    fontWeight: tokens.fontWeight.semibold,
                    color: theme.palette.text.primary,
                  }}
                  scope="col"
                >
                  {column.sortable ? (
                    <TableSortLabel
                      active={sortBy === column.key}
                      direction={sortBy === column.key ? sortDir : 'asc'}
                      onClick={() => handleSort(column.key)}
                      sx={{
                        '&:focus-visible': {
                          outline: `2px solid ${theme.palette.primary.main}`,
                          outlineOffset: 2,
                        },
                      }}
                    >
                      {column.header}
                      {sortBy === column.key && (
                        <Box component="span" sx={{ position: 'absolute', clip: 'rect(0 0 0 0)' }}>
                          {sortDir === 'desc' ? 'sorted descending' : 'sorted ascending'}
                        </Box>
                      )}
                    </TableSortLabel>
                  ) : (
                    column.header
                  )}
                </TableCell>
              ))}
              {actionsSlot && (
                <TableCell align="right" sx={{ fontWeight: tokens.fontWeight.semibold }} scope="col">
                  Actions
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const rowId = getRowId(row);
              const isSelected = selected.includes(rowId);

              return (
                <TableRow
                  key={rowId}
                  hover
                  selected={isSelected}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '&.Mui-selected': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.08),
                    },
                    '&.Mui-selected:hover': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.12),
                    },
                    '&:focus-visible': {
                      outline: `2px solid ${theme.palette.primary.main}`,
                      outlineOffset: -2,
                    },
                  }}
                  tabIndex={0}
                  aria-label={`Row ${index + 1}`}
                >
                  {onSelectRows && (
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={isSelected}
                        onChange={() => handleSelectRow(rowId)}
                        inputProps={{
                          'aria-label': `Select row ${rowId}`,
                        }}
                        sx={{
                          '&:focus-visible': {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                          },
                        }}
                      />
                    </TableCell>
                  )}
                  {columns.map((column) => (
                    <TableCell
                      key={String(column.key)}
                      align={column.align || 'left'}
                      sx={{ py: 1 }}
                    >
                      {column.render
                        ? column.render(row)
                        : String(row[column.key] ?? '')}
                    </TableCell>
                  ))}
                  {actionsSlot && (
                    <TableCell align="right" sx={{ py: 1 }}>
                      {actionsSlot(row)}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Box>

      <TablePagination
        component="div"
        count={total}
        page={page}
        rowsPerPage={pageSize}
        onPageChange={(event, newPage) => onPageChange(newPage)}
        onRowsPerPageChange={(event) => {
          onPageSizeChange(parseInt(event.target.value, 10));
        }}
        rowsPerPageOptions={[10, 25, 50, 100]}
        labelRowsPerPage="Rows per page:"
        labelDisplayedRows={({ from, to, count }) =>
          `${from}–${to} of ${count !== -1 ? count : `more than ${to}`}`
        }
        sx={{
          borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            marginTop: 2,
            marginBottom: 2,
          },
        }}
      />
    </Paper>
  );
};

DataTable.propTypes = {
  /** Array of column definitions */
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      header: PropTypes.string.isRequired,
      width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      align: PropTypes.oneOf(['left', 'center', 'right']),
      sortable: PropTypes.bool,
      render: PropTypes.func,
    })
  ).isRequired,
  /** Array of data rows */
  rows: PropTypes.array.isRequired,
  /** Total number of rows (for pagination) */
  total: PropTypes.number.isRequired,
  /** Current page (0-indexed) */
  page: PropTypes.number.isRequired,
  /** Number of rows per page */
  pageSize: PropTypes.number.isRequired,
  /** Current sort column key */
  sortBy: PropTypes.string,
  /** Current sort direction */
  sortDir: PropTypes.oneOf(['asc', 'desc']),
  /** Loading state */
  loading: PropTypes.bool,
  /** Error message */
  error: PropTypes.string,
  /** Callback when page changes */
  onPageChange: PropTypes.func.isRequired,
  /** Callback when page size changes */
  onPageSizeChange: PropTypes.func.isRequired,
  /** Callback when sort changes */
  onSortChange: PropTypes.func,
  /** Callback when row selection changes (enables selection checkboxes) */
  onSelectRows: PropTypes.func,
  /** Function to extract unique ID from row */
  getRowId: PropTypes.func.isRequired,
  /** Function to render action buttons for each row */
  actionsSlot: PropTypes.func,
  /** Message to display when table is empty */
  emptyMessage: PropTypes.string,
  /** Accessible label for the table */
  ariaLabel: PropTypes.string,
};

export default DataTable;
